import React from 'react';
import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import {
  DRIVER_APP_NAME,
  DRIVER_ROUTES,
  DRIVER_SUPPORT,
} from '../data/driverAppSupport';
import '../styles/DriverLegal.css';

const DriverPrivacyPolicy = () => {
  const lastUpdated = '4 October 2026';

  return (
    <main className="driver-legal-page">
      <SeoHead
        title="EasyDropTaxi Driver App Privacy Policy"
        description="Privacy Policy for the EasyDropTaxi Driver App, explaining what information is used for trip operations, odometer verification, and driver support."
        path={DRIVER_ROUTES.privacy}
      />

      <section className="page-banner block-space text-white" style={{ backgroundColor: 'var(--secondary-color)' }}>
        <div className="container">
          <h1 className="display-4 fw-bold">Driver Privacy Policy</h1>
          <p className="lead mb-0">
            Privacy information for authorized users of the {DRIVER_APP_NAME} App.
          </p>
        </div>
      </section>

      <section className="block-space">
        <div className="container">
          <div className="driver-legal-content">
            <p>
              This Privacy Policy explains how EasyDropTaxi (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;)
              handles information in connection with the {DRIVER_APP_NAME} mobile application
              (the &quot;App&quot;). The App is an operational tool for authorized EasyDropTaxi drivers.
            </p>
            <p>
              This policy applies to the Driver App only. It does not replace the privacy practices
              that apply to the EasyDropTaxi customer website or other EasyDropTaxi applications.
            </p>
            <p><strong>Last updated:</strong> {lastUpdated}</p>

            <h2>Information We May Collect</h2>
            <p>
              Depending on how you use the App, we may collect or process the following information
              as part of normal Driver App operation:
            </p>
            <ul>
              <li>Driver name</li>
              <li>Registered mobile number</li>
              <li>Driver account / login information</li>
              <li>Vehicle information linked to the driver account</li>
              <li>Booking information for assigned trips</li>
              <li>Pickup and drop information</li>
              <li>Trip date and time</li>
              <li>Trip status</li>
              <li>Start KM and closing KM</li>
              <li>Odometer photographs</li>
              <li>Customer collection / payment information entered through the Driver App</li>
              <li>Other operational information required to run and support the Driver App</li>
            </ul>

            <h2>Camera / Photo Usage</h2>
            <p>
              The App may request access to your device camera or photo library so you can capture
              and submit trip-related photographs when required, including:
            </p>
            <ul>
              <li>Opening odometer photograph</li>
              <li>Closing odometer photograph</li>
              <li>Other trip-related photographs where required for verification</li>
            </ul>
            <div className="driver-legal-note">
              <p>
                Camera or photo access is used only when you choose to capture or upload a required
                photograph. The App does not claim continuous camera access or background camera recording.
              </p>
            </div>

            <h2>How Information Is Used</h2>
            <p>Information processed through the Driver App may be used for:</p>
            <ul>
              <li>Driver authentication and account access</li>
              <li>Assigned booking management</li>
              <li>Trip management</li>
              <li>Trip start and end recording</li>
              <li>Odometer verification</li>
              <li>Customer collection / payment recording</li>
              <li>Operational communication related to assigned work</li>
              <li>Security and fraud prevention</li>
              <li>Technical support and troubleshooting</li>
              <li>Business record keeping</li>
            </ul>

            <h2>Customer Information</h2>
            <p>
              Drivers may receive limited customer and booking information that is reasonably
              required to perform an assigned trip. That information is provided only for legitimate
              trip-related purposes.
            </p>
            <p>
              Drivers must not misuse, copy, distribute, sell, publish, or disclose customer
              information without authorization from EasyDropTaxi.
            </p>

            <h2>Third-Party Services</h2>
            <p>
              The production EasyDropTaxi platform uses Google Firebase services to support
              application operations. In connection with the Driver App, this may include services
              used for authentication, operational data storage, and storage of trip-related
              photographs such as odometer images.
            </p>
            <p>
              Those providers process information according to their own terms and privacy policies,
              in addition to this Privacy Policy.
            </p>

            <h2>Data Security</h2>
            <p>
              We apply reasonable technical and organizational measures designed to protect
              information against unauthorized access, misuse, loss, or alteration. No method of
              transmission or storage is completely secure, and we do not claim that information
              will always be 100% secure.
            </p>

            <h2>Data Retention</h2>
            <p>
              Information may be retained for as long as reasonably necessary for operational,
              legal, accounting, security, fraud-prevention, and dispute-resolution requirements,
              and for other legitimate business record-keeping purposes.
            </p>

            <h2>Account / Data Deletion</h2>
            <p>
              Drivers may request deletion of their Driver App account and applicable associated
              personal information. Details of the request process, verification steps, and
              retention limitations are explained here:
            </p>
            <p>
              <Link to={DRIVER_ROUTES.dataDeletion}>
                EasyDropTaxi Driver — Account &amp; Data Deletion
              </Link>
            </p>

            <h2>Contact</h2>
            <p>
              For privacy questions related to the {DRIVER_APP_NAME} App, contact EasyDropTaxi support:
            </p>
            <div className="driver-contact-panel">
              <p>
                <strong>Phone:</strong>{' '}
                <a href={DRIVER_SUPPORT.phoneTel}>{DRIVER_SUPPORT.phoneDisplay}</a>
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href={DRIVER_SUPPORT.mailto}>{DRIVER_SUPPORT.email}</a>
              </p>
              <p className="mb-0">
                <strong>Address:</strong> {DRIVER_SUPPORT.address}
              </p>
            </div>

            <div className="driver-legal-actions">
              <Link to={DRIVER_ROUTES.terms} className="btn driver-btn-outline">
                Driver Terms &amp; Conditions
              </Link>
              <Link to={DRIVER_ROUTES.support} className="btn driver-btn-primary">
                Driver Support
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default DriverPrivacyPolicy;
